import type { ParsedUrlQuery } from 'querystring';
import type { UrlObject } from 'url';

export type QueryParamSimpleValueType = string | number | boolean | undefined | null;
export type DefineQueryParamSimpleValueType = string | number | boolean;
export type QueryParamValueType = QueryParamSimpleValueType | QueryParamSimpleValueType[];
export type QueryParamsMapType = Record<string, QueryParamValueType>;

const isValueDefined = (value: QueryParamValueType): boolean =>
  value !== undefined && value !== null;

export function prependNonEmpty(str: string, pre: string): string {
  return str ? `${pre}${str}` : '';
}

export function getSearchFromParams(params: QueryParamsMapType = {}): string {
  // filter out empty keys
  const searchParams = new URLSearchParams();
  Object.entries(params)
    .filter(([key, value]) => !!key && isValueDefined(value))
    .forEach(([key, value]) => {
      if (!Array.isArray(value)) {
        searchParams.append(key, (value as DefineQueryParamSimpleValueType).toString());
        return;
      }
      value
        .filter(isValueDefined)
        .forEach(sub =>
          searchParams.append(key, (sub as DefineQueryParamSimpleValueType).toString()),
        );
    });

  return prependNonEmpty(searchParams.toString(), '?');
}

export function getParamsFromSearch(search: string) {
  // filter out empty keys
  const searchParams = new URLSearchParams(search);
  // @ts-ignore https://github.com/Microsoft/TypeScript/issues/23174
  return Object.fromEntries(searchParams.entries());
}

export function getPathWithSearch({
  path,
  params,
}: {
  path: string;
  params: QueryParamsMapType;
}): string {
  return `${path}${getSearchFromParams(params)}`;
}

export function replaceQueryParamMap({
  search,
  queryMap,
  blacklist,
  whitelist,
}: {
  search: string;
  queryMap: QueryParamsMapType;
  blacklist?: string[];
  whitelist?: string[];
}): string {
  const searchParams = new URLSearchParams(search);

  Object.entries(queryMap).forEach(([queryName, value]) => {
    if (!value) {
      searchParams.delete(queryName);
    } else if (searchParams.has(queryName)) {
      searchParams.set(queryName, value.toString());
    } else {
      searchParams.append(queryName, value.toString());
    }
  });

  if (blacklist) {
    blacklist.forEach(key => searchParams.delete(key));
  }

  if (whitelist) {
    // @ts-ignore https://github.com/Microsoft/TypeScript/issues/23174
    // eslint-disable-next-line no-restricted-syntax
    for (const pair of searchParams.entries()) {
      if (whitelist.findIndex(v => v === pair[0]) > -1) continue;

      searchParams.delete(pair[0]);
    }
  }

  return prependNonEmpty(searchParams.toString(), '?');
}

export function parseUrl(url: string): { pathname: string; search: string; hastag: string } {
  const matched = url.match(/([^?#]*)\??([^#]*)#?(.*)/);

  if (!matched) return { pathname: '', search: '', hastag: '' };

  return {
    pathname: matched[1] || '',
    search: prependNonEmpty(matched[2] || '', '?'),
    hastag: prependNonEmpty(matched[3] || '', '#'),
  };
}

export function replaceQueryParam2(
  pathname: string,
  query: ParsedUrlQuery,
  asPath: string,
  queryName: string,
  value: string | number | undefined | null,
) {
  return {
    href: replaceQueryParamHrefOnly(pathname, query, queryName, value),
    as: replaceQueryParamAsOnly(asPath, queryName, value),
  };
}

export function replaceQueryParamHrefOnly(
  pathname: string,
  query: ParsedUrlQuery,
  queryName: string,
  value: string | number | undefined | null,
) {
  const currentPathname = pathname;
  const { [queryName]: _, ...otherQueryParams } = query;
  const newQuery = value ? { ...otherQueryParams, [queryName]: value } : otherQueryParams;

  const href: UrlObject = {
    pathname: currentPathname,
    query: newQuery,
  };
  return href;
}

export function replaceQueryParamAsOnly(
  asPath: string,
  queryName: string,
  value: string | number | undefined | null,
) {
  const { pathname: asPathname, search } = parseUrl(asPath);
  const params = new URLSearchParams(search);
  if (value) {
    params.set(queryName, value.toString());
  } else {
    params.delete(queryName);
  }
  const asQuery = Object.fromEntries(params.entries());

  const as: UrlObject = {
    pathname: asPathname,
    query: asQuery,
  };
  return as;
}

export function urlObjectToString(url: UrlObject | string) {
  if (typeof url === 'string') return url;
  let urlStr = url.pathname;
  const query = url.query as string | Record<string, string> | null | undefined;
  const hasQueries = query && typeof query === 'object' ? !!Object.keys(query).length : !!query;
  if (hasQueries) {
    urlStr = `${urlStr}?${new URLSearchParams(query || undefined).toString()}`;
  }
  // Complete as you need. For now, it's enough. We just use pathname and query above, but an UrlObject can have more stuff inside.
  return urlStr || '';
}

// Test cases and function generated with the help of ChatGPT. The code might not be 100% clean :)
export function formatNextjsUrl(href: string, params: Record<string, any> = {}): string {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    const paramKey = `[${key}]`;
    if (href.includes(paramKey)) {
      const regex = new RegExp(`\\${paramKey}(\\??)`);
      href = href.replace(regex, encodeURIComponent(value) + '$1');
    } else {
      query.set(key, value);
    }
  }

  if (href.includes('[')) {
    const missingParam = href.match(/\[([^\]]+)\]/)?.[1];
    throw new Error(`Required parameter "${missingParam}" is missing`);
  }

  const queryString = query.toString();
  if (queryString) {
    const prefix = href.includes('?') ? '&' : '?';
    href += `${prefix}${queryString}`;
  }

  if (!href.startsWith('/')) href = `/${href}`;

  return href;
}
